import { IconNames } from '@blueprintjs/icons';
import { USStateNames } from '@strata/constants';
import { formatDate } from '@strata/helper-functions';
import { Button } from '@strata/react-buttons';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import { type ColumnDef, GridWithSearch, PrimaryData, SecondaryData } from '@strata/react-grid';
import { PageTitle } from '@strata/react-page-title';
import { Icon } from '@strata/react-status-icons';
import { type ComponentProps, type Dispatch, type SetStateAction, useState } from 'react';
import { type NavigateFunction, useNavigate } from 'react-router';

import { DeleteListDialog } from 'components/admin/dialogs/DeleteListDialog';
import { usePermissionCheck } from 'services/APIHooks';
import { Permissions } from 'services/constants';
import { AppRoutes } from 'services/routes';
import { type StateList, useGetStateListsQuery } from 'store/api/auctionApi';
import { useLoggedInSupplierName } from 'store/auth/authThunks';

const customSearchValueHandlers: ComponentProps<
    typeof GridWithSearch<StateList>
>['customSearchValueHandlers'] = {
    states: (states) => states?.map((state) => `${state} ${USStateNames[state]}`).join(' '),
};

const columns = ({
    navigate,
    userCanEdit,
    setStateListToDelete,
}: {
    navigate: NavigateFunction;
    userCanEdit: boolean;
    setStateListToDelete: Dispatch<SetStateAction<StateList | undefined>>;
}): ColumnDef<StateList>[] => [
    {
        accessorKey: 'listName',
        header: 'State List Name',
        cell: ({ getValue }) => <PrimaryData data={getValue()} />,
    },
    {
        accessorKey: 'states',
        header: 'Selected States',
        cell: ({ getValue }) => (
            <SecondaryData
                data={`${getValue()?.length ?? 0} State${getValue()?.length === 1 ? '' : 's'}`}
                customTooltipContent={
                    <ul className="padding__left--medium" style={{ margin: 0 }}>
                        {getValue()?.map((state) => (
                            <li key={state}>{USStateNames[state]}</li>
                        ))}
                    </ul>
                }
            />
        ),
    },
    {
        accessorKey: 'updatedBy',
        header: 'Updated By',
    },
    {
        accessorKey: 'lastUpdated',
        header: 'Last Updated',
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        accessorKey: 'stateListId',
        header: 'Actions',
        enableSorting: false,
        size: 70,
        cell: ({ getValue, row }) => (
            <div className="sds_grid-actions-row">
                <Icon
                    icon={IconNames.EDIT}
                    intent={Icon.Intent.SECONDARY}
                    title="Edit"
                    onClick={() => navigate(AppRoutes.stateListDetails(getValue()))}
                />
                {userCanEdit && (
                    <Icon
                        icon={IconNames.TRASH}
                        intent={Icon.Intent.SECONDARY}
                        title="Delete"
                        onClick={() => setStateListToDelete(row.original)}
                    />
                )}
            </div>
        ),
    },
];

export const StateLists = () => {
    const navigate = useNavigate();
    const userCanEdit = usePermissionCheck(Permissions.WRITE_AUCTIONS);
    const loggedInSupplierName = useLoggedInSupplierName();

    const { data: stateLists, isFetching, refetch } = useGetStateListsQuery();

    const [stateListToDelete, setStateListToDelete] = useState<StateList>();

    return (
        <FormPageContainer>
            <div
                className="flex__gap--medium padding__around--medium"
                style={{ flexDirection: 'column', height: '100%' }}
            >
                <PageTitle title="State Lists" secondaryTitle={loggedInSupplierName} />
                <DeleteListDialog
                    listType={DeleteListDialog.ListType.STATE}
                    listToDelete={stateListToDelete}
                    onClose={() => setStateListToDelete(undefined)}
                    onDeleteSuccess={() => {
                        void refetch();
                    }}
                />
                <div style={{ overflow: 'auto', flexGrow: 1 }}>
                    <GridWithSearch
                        data={stateLists}
                        isLoading={isFetching}
                        sortProperties={[{ id: 'listName', desc: false }]}
                        customSearchValueHandlers={customSearchValueHandlers}
                        rightHeaderContent={
                            <Button
                                label="Add State List"
                                id="add-state-list-button"
                                intent={Button.Intent.PRIMARY}
                                disabled={!userCanEdit}
                                onClick={() => navigate(AppRoutes.stateListDetails('create'))}
                            />
                        }
                        columns={columns({ navigate, userCanEdit, setStateListToDelete })}
                    />
                </div>
            </div>
        </FormPageContainer>
    );
};
